'use client';

import { usePathname } from 'next/navigation';
import { usePhrasesStore } from '@/store';
import { JSXFromString } from '@/utils/JSXFromString';
import { replaceVarsOnTranslation } from '@/utils/replaceVarsOnTranslation';
import { isEmpty } from '@/utils/fp';
import { config } from '@/config';
import { usePhrasesContext } from '@/context/PhrasesContext/PhrasesContext';
import { useHasHydrated } from '@/hooks/general/useHasHydrated';
import { logger } from '@/utils/logger/logger';
import { noPhraseSymbol } from '@/consts/noPhraseSymbol';
import { emptyPhrase } from '@/utils/phrases/isEmptyPhrase';
import * as Sentry from '@sentry/nextjs';
import React from 'react';

type PhraseData = Record<number, string>;

interface DebugData {
    path: string;
}

export interface TranslateOptions {
    replaceTextVars?: (string | number)[];
    asString?: boolean;
}

const isTestEnv = config.nodeEnv === 'test';

const missingPhrases = new Set();

const useHydratedPhraseDataFromStoreOrContext = () => {
    const hydrated = useHasHydrated();
    const phraseDataFromContext = usePhrasesContext();
    const phraseDataFromStore = usePhrasesStore((state) => state.phrases);
    const sortedPhraseDataFromStore = {
        ...phraseDataFromStore?.main,
        ...phraseDataFromStore?.subpanel,
    };
    return hydrated ? sortedPhraseDataFromStore : phraseDataFromContext || {};
};

const checkForMissingPhrase = (phraseId: number, store: PhraseData, debugData: DebugData) => {
    if (!missingPhrases.has(phraseId) && !isEmpty(store)) {
        logger.error(
            `phraseId: ${phraseId}, store: ${JSON.stringify(store)}, debugData: ${JSON.stringify(
                debugData,
            )}`,
        );
        Sentry.captureException(new Error('Missing PhraseId - failed to render on the client'), {
            extra: {
                phraseId,
                ...debugData,
            },
        });
        missingPhrases.add(phraseId);
    }
};

const phraseFromStore =
    (store: PhraseData) =>
    (phraseId: number): string | undefined =>
        store[phraseId];

const getPhrase = (
    phraseId: number,
    primaryPhraseStore: PhraseData,
    debugData: DebugData,
    options?: {
        replaceTextVars?: (string | number)[];
        asString?: boolean;
    },
) => {
    const findPhrase = phraseFromStore(primaryPhraseStore);
    const phrase = findPhrase(phraseId);
    if (!phrase) {
        checkForMissingPhrase(phraseId, primaryPhraseStore, debugData);
        return emptyPhrase;
    }

    if (phrase === noPhraseSymbol) return options?.asString ? '' : emptyPhrase;

    if (options?.replaceTextVars) {
        const phraseWithReplacedVars = replaceVarsOnTranslation(phrase, options.replaceTextVars);
        return options?.asString ? phraseWithReplacedVars : JSXFromString(phraseWithReplacedVars);
    }

    return options?.asString ? phrase : JSXFromString(phrase);
};

const findPhrase = (
    phraseId: number,
    phraseData: PhraseData,
    debugData: DebugData,
    options?: { replaceTextVars?: (string | number)[]; asString?: boolean },
): ReturnType<typeof getPhrase> => {
    return isTestEnv ? String(phraseId) : getPhrase(phraseId, phraseData, debugData, options);
};

export const usePhrase = () => {
    const pathname = usePathname();

    const debugData = { path: pathname ?? '' };
    const phraseData = useHydratedPhraseDataFromStoreOrContext();

    const getPhrase = <B extends boolean>(
        phraseId: number | undefined,
        options?: Omit<TranslateOptions, 'asString'> & { asString?: B },
    ): B extends true ? string : string | React.ReactElement | React.ReactElement[] => {
        if (!phraseId) {
            return emptyPhrase;
        } else {
            return findPhrase(phraseId, phraseData, debugData, options) as B extends true
                ? string
                : React.ReactElement | React.ReactElement[];
        }
    };

    return { t: getPhrase };
};
